(() => {
  const documentRoot = document.documentElement;
  if (documentRoot.dataset.stoneHausProductArrangement === 'true') return;
  documentRoot.dataset.stoneHausProductArrangement = 'true';

  const normalise = value => (value || '').replace(/\s+/g, ' ').trim();
  const observedAfterpayRoots = new WeakSet();
  const wastageStates = new WeakMap();

  const stateFor = info => {
    let state = wastageStates.get(info);
    if (!state) {
      state = { baseQuantity: null, checked: false, applying: false };
      wastageStates.set(info, state);
    }
    return state;
  };

  const addCompactWastageStyles = () => {
    if (document.getElementById('StoneHausCompactWastageStyles')) return;

    const style = document.createElement('style');
    style.id = 'StoneHausCompactWastageStyles';
    style.textContent = `
      .wt-product__info .wastage-toggle,
      .wt-product__info #ss-upsell-2,
      .wt-product__info .sh-order-extras {
        display: none !important;
      }
      .sh-wastage-inline {
        margin: 6px 0 15px;
        width: 100%;
      }
      .sh-wastage-inline__label {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 14px;
        min-height: 34px;
        padding: 3px 0;
        cursor: pointer;
        color: #171717;
      }
      .sh-wastage-inline__copy {
        display: flex;
        align-items: baseline;
        flex-wrap: wrap;
        gap: 2px 8px;
        min-width: 0;
      }
      .sh-wastage-inline__title {
        font-size: 13px;
        line-height: 1.3;
        font-weight: 650;
      }
      .sh-wastage-inline__note {
        font-size: 11px;
        line-height: 1.3;
        color: #727572;
      }
      .sh-wastage-inline__input {
        position: absolute !important;
        width: 1px !important;
        height: 1px !important;
        margin: -1px !important;
        padding: 0 !important;
        overflow: hidden !important;
        clip: rect(0 0 0 0) !important;
        white-space: nowrap !important;
        border: 0 !important;
      }
      .sh-wastage-inline__switch {
        position: relative;
        flex: 0 0 36px;
        width: 36px;
        height: 20px;
        border: 1px solid #c9cbc7;
        border-radius: 999px;
        background: #eceeea;
        transition: background .18s ease, border-color .18s ease;
      }
      .sh-wastage-inline__switch::after {
        content: '';
        position: absolute;
        top: 2px;
        left: 2px;
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: #fff;
        box-shadow: 0 1px 3px rgba(0,0,0,.18);
        transition: transform .18s ease;
      }
      .sh-wastage-inline__input:checked + .sh-wastage-inline__switch {
        background: #B5D1CE;
        border-color: #8eaaa7;
      }
      .sh-wastage-inline__input:checked + .sh-wastage-inline__switch::after {
        transform: translateX(16px);
      }
      .sh-wastage-inline__input:focus-visible + .sh-wastage-inline__switch {
        outline: 2px solid #171717;
        outline-offset: 2px;
      }
      @media (max-width: 749px) {
        .sh-wastage-inline { margin-top: 5px; }
        .sh-wastage-inline__note { font-size: 10.5px; }
      }
      @media (prefers-reduced-motion: reduce) {
        .sh-wastage-inline__switch,
        .sh-wastage-inline__switch::after { transition: none; }
      }
    `;
    document.head.appendChild(style);
  };

  const updateAfterpayMessage = () => {
    document.querySelectorAll('square-placement').forEach(placement => {
      const root = placement.shadowRoot;
      if (!root) return;

      const applyCopy = () => {
        const message = root.querySelector('.afterpay-text2');
        if (message && normalise(message.textContent) !== 'available up to $4000') {
          message.textContent = 'available up to $4000 ';
        }
      };

      applyCopy();

      if (!observedAfterpayRoots.has(root)) {
        observedAfterpayRoots.add(root);
        const observer = new MutationObserver(applyCopy);
        observer.observe(root, { childList: true, subtree: true, characterData: true });
      }
    });
  };

  const pricingLabelReplacements = new Map([
    ['Walling / Per m2', 'Walling — priced per m²'],
    ['Corners / Per Lm', 'Matching corners — priced per lineal metre']
  ]);

  const renamePricingLabels = info => {
    const options = info.querySelector('.wt-product__options');
    if (!options) return;

    options.querySelectorAll(
      '.wt-product__option__title .value, .wt-product__option__body label, .wt-product__option__dropdown span'
    ).forEach(element => {
      const replacement = pricingLabelReplacements.get(normalise(element.textContent));
      if (!replacement) return;

      element.textContent = replacement;
      element.classList.remove('sh-variant-choice');
    });
  };

  const quantityFor = info =>
    info.querySelector('input[name="quantity"], .js-counter-quantity');

  const stoneVariantOptionFor = info =>
    Array.from(info.querySelectorAll('.wt-product__option')).find(option => {
      const optionName = normalise(
        option.querySelector('.product-form__input-title')?.textContent ||
        option.querySelector('.wt-product__option__title .label')?.textContent
      ).replace(/:\s*$/, '');

      return /^Stone Variant$/i.test(optionName) || /\bStone Variant\b/i.test(normalise(option.textContent));
    });

  const updateQuantityUnit = info => {
    const unitLabels = info.querySelectorAll('.quantity-unit-label');
    if (!unitLabels.length) return;

    const stoneVariantOption = stoneVariantOptionFor(info);
    if (!stoneVariantOption) return;

    const selectedValue = normalise(
      stoneVariantOption.querySelector('input:checked')?.value ||
      stoneVariantOption.querySelector('.wt-product__option__title .value')?.textContent
    );
    const nextUnit = /(corner|lineal|per\s*l\.?\s*m\.?)/i.test(selectedValue) ? 'lm' : 'm²';

    unitLabels.forEach(label => {
      if (normalise(label.textContent) !== nextUnit) label.textContent = nextUnit;
    });
  };

  const updateDisplayedPriceUnit = info => {
    const stoneVariantOption = stoneVariantOptionFor(info);
    if (!stoneVariantOption) return;

    const selectedValue = normalise(
      stoneVariantOption.querySelector('input:checked')?.value ||
      stoneVariantOption.querySelector('.wt-product__option__title .value')?.textContent
    );
    const nextUnit = /(corner|lineal|per\s*l\.?\s*m\.?)/i.test(selectedValue) ? 'Lm' : 'm²';

    info.querySelectorAll('.wt-product__price__final').forEach(price => {
      const currentText = normalise(price.textContent);
      const amount = currentText.match(/(?:A\$|\$)\s?[\d,.]+/)?.[0];
      if (!amount) return;

      const cleanAmount = amount.replace(/\.00$/, '');
      const nextText = `${cleanAmount}/${nextUnit}`;
      if (currentText !== nextText) price.textContent = nextText;
    });
  };

  const placeStoneVariantsBelowPickup = info => {
    const optionsBlock = info.querySelector('[data-block-id="variant_picker"]');
    const pickup = info.querySelector('pickup-availability');
    if (!optionsBlock || !pickup) return;

    optionsBlock.classList.add('sh-variants-below-pickup');
    if (pickup.nextElementSibling !== optionsBlock) {
      pickup.insertAdjacentElement('afterend', optionsBlock);
    }
  };

  const moveProjectVideosBelowActions = info => {
    const saveButton = info.querySelector('.stonehaus-save-button--product');
    const video = Array.from(document.querySelectorAll('[class*="video-template--"]')).find(element =>
      /Real-life Projects/i.test(element.textContent || '')
    );
    if (!saveButton || !video) return;

    const actions = saveButton.closest('.sh-cladding-actions');
    const insertionTarget = actions || saveButton;
    if (insertionTarget.nextElementSibling !== video) {
      insertionTarget.insertAdjacentElement('afterend', video);
    }

    requestAnimationFrame(() => {
      const slider = video.querySelector('.swiper');
      if (slider && slider.swiper) slider.swiper.update();
    });
  };

  const removeDescriptionSampleLinks = info => {
    if (!info.querySelector('.sh-cladding-actions')) return;

    info.querySelectorAll('[data-block-id="description"] a, .wt-description a').forEach(link => {
      if (!/order\s+(a\s+)?samples?/i.test(normalise(link.textContent))) return;
      const paragraph = link.closest('p');
      link.remove();
      if (paragraph && normalise(paragraph.textContent) === '') paragraph.remove();
    });
  };

  const placePaymentIcons = info => {
    const paymentBadge = info.querySelector('.wt-product__payments-badge');
    const shopPayButton = info.querySelector('.shopify-payment-button');
    if (!paymentBadge || !shopPayButton) return;

    const paymentBlock = paymentBadge.closest('[data-block-id]') || paymentBadge.parentElement;
    if (!paymentBlock) return;

    if (shopPayButton.nextElementSibling !== paymentBlock) {
      shopPayButton.insertAdjacentElement('afterend', paymentBlock);
    }
  };

  const placeSavedStone = info => {
    const saveButton = info.querySelector('.stonehaus-save-button--product');
    if (!saveButton) return;

    const sampleControls = Array.from(info.querySelectorAll('a, button')).filter(element =>
      /^order\s+(a\s+)?samples?$/i.test(normalise(element.textContent))
    );
    const sampleControl =
      sampleControls.find(element => element.closest('.sh-cladding-actions')) ||
      sampleControls.find(element => element.closest('.wt-button-section__container')) ||
      sampleControls.find(element => element.offsetParent !== null) ||
      sampleControls[0];
    if (!sampleControl) return;

    const sampleContainer = sampleControl.closest('.sh-cladding-actions')
      ? sampleControl
      : sampleControl.closest('.wt-button-section__container') || sampleControl.parentElement || sampleControl;
    if (sampleContainer.nextElementSibling !== saveButton) {
      sampleContainer.insertAdjacentElement('afterend', saveButton);
    }
  };

  const cleanupLegacyExtras = info => {
    info.querySelectorAll('.sh-order-extras').forEach(element => element.remove());
    info.querySelectorAll('.wastage-toggle, #ss-upsell-2').forEach(element => element.remove());

    info.querySelectorAll('[class*="upsell-heading-"]').forEach(heading => {
      if (!/PAIR IT WITH/i.test(normalise(heading.textContent))) return;

      const upsellRoot = heading.closest('[class*="section-"][class*="upsell-"]');
      if (upsellRoot && info.contains(upsellRoot)) {
        upsellRoot.remove();
        return;
      }

      const fallbackRoot = heading.parentElement?.parentElement;
      if (fallbackRoot && info.contains(fallbackRoot)) fallbackRoot.remove();
    });
  };

  const ensureCompactWastage = info => {
    cleanupLegacyExtras(info);

    const picker = info.querySelector('[data-block-id="variant_picker"]');
    const stoneVariantOption = stoneVariantOptionFor(info);
    const insertionTarget = stoneVariantOption || picker;
    if (!insertionTarget) return;

    let control = info.querySelector('[data-stonehaus-wastage-inline]');
    if (!control) {
      control = document.createElement('div');
      control.className = 'sh-wastage-inline';
      control.setAttribute('data-stonehaus-wastage-inline', '');
      control.innerHTML = `
        <label class="sh-wastage-inline__label">
          <span class="sh-wastage-inline__copy">
            <span class="sh-wastage-inline__title">Add 10% extra for wastage</span>
            <span class="sh-wastage-inline__note">Recommended for cuts &amp; breakage</span>
          </span>
          <input class="sh-wastage-inline__input" type="checkbox" data-stonehaus-wastage-toggle aria-label="Add 10% extra for wastage">
          <span class="sh-wastage-inline__switch" aria-hidden="true"></span>
        </label>
      `;
    }

    if (insertionTarget.nextElementSibling !== control) {
      insertionTarget.insertAdjacentElement('afterend', control);
    }

    const quantity = quantityFor(info);
    const toggle = control.querySelector('[data-stonehaus-wastage-toggle]');
    const state = stateFor(info);

    if (quantity && state.baseQuantity === null) {
      state.baseQuantity = Math.max(1, Number(quantity.value) || 1);
    }
    if (toggle) toggle.checked = state.checked;
  };

  const arrangeInfo = info => {
    cleanupLegacyExtras(info);
    updateAfterpayMessage();
    renamePricingLabels(info);
    updateQuantityUnit(info);
    updateDisplayedPriceUnit(info);
    placeStoneVariantsBelowPickup(info);
    ensureCompactWastage(info);
    placePaymentIcons(info);
    removeDescriptionSampleLinks(info);
    placeSavedStone(info);
    moveProjectVideosBelowActions(info);
  };

  const arrange = () => {
    addCompactWastageStyles();
    document.querySelectorAll('.wt-product__info').forEach(arrangeInfo);
  };

  document.addEventListener('change', event => {
    const info = event.target.closest?.('.wt-product__info');
    if (!info) return;

    const quantity = quantityFor(info);
    const state = stateFor(info);

    if (event.target.matches?.('[data-stonehaus-wastage-toggle]')) {
      if (!quantity) return;

      const checked = event.target.checked;
      if (checked) {
        state.baseQuantity = Math.max(1, Number(quantity.value) || state.baseQuantity || 1);
      }
      state.checked = checked;

      const nextQuantity = checked
        ? Math.ceil((state.baseQuantity || 1) * 1.1)
        : (state.baseQuantity || 1);

      state.applying = true;
      quantity.value = String(nextQuantity);
      quantity.dispatchEvent(new Event('input', { bubbles: true }));
      quantity.dispatchEvent(new Event('change', { bubbles: true }));
      state.applying = false;
      return;
    }

    if (quantity && event.target === quantity && !state.checked && !state.applying) {
      state.baseQuantity = Math.max(1, Number(quantity.value) || 1);
    }

    requestAnimationFrame(() => {
      updateQuantityUnit(info);
      updateDisplayedPriceUnit(info);
      placeStoneVariantsBelowPickup(info);
      ensureCompactWastage(info);
    });
  }, true);

  document.addEventListener('input', event => {
    const info = event.target.closest?.('.wt-product__info');
    if (!info) return;
    const quantity = quantityFor(info);
    const state = stateFor(info);
    if (quantity && event.target === quantity && !state.checked && !state.applying) {
      state.baseQuantity = Math.max(1, Number(quantity.value) || 1);
    }
  }, true);

  const start = () => {
    arrange();

    let afterpayAttempts = 0;
    const afterpayTimer = setInterval(() => {
      updateAfterpayMessage();
      afterpayAttempts += 1;
      if (afterpayAttempts >= 40) clearInterval(afterpayTimer);
    }, 250);

    const observer = new MutationObserver(() => requestAnimationFrame(arrange));
    observer.observe(document.body, { childList: true, subtree: true });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', arrange);
})();
